import {
	createTripWorkspace,
	isTripWorkspace,
	type TripWorkspace,
} from "./trip";
export type TripShare = {
	kind: "trip";
	trip: TripWorkspace;
	shareId: string;
	revision: number;
};
export type ReplyShare = {
	kind: "reply";
	shareId: string;
	nick: string;
	marks: [string, "yes" | "skip"][];
};
export type PaymentShare = {
	kind: "payment";
	rows: { from: string; to: string; amountJpy: number }[];
};
export type SharePayload = TripShare | ReplyShare | PaymentShare;
const encoder = new TextEncoder();
function base64(bytes: Uint8Array) {
	let s = "";
	bytes.forEach((x) => {
		s += String.fromCharCode(x);
	});
	return btoa(s).replaceAll("+", "-").replaceAll("/", "_").replace(/=+$/, "");
}
function bytes(s: string) {
	if (!/^[A-Za-z0-9_-]+$/.test(s) || s.length > 100000)
		throw new Error("INVALID_SHARE");
	return Uint8Array.from(
		atob(s.replaceAll("-", "+").replaceAll("_", "/")),
		(c) => c.charCodeAt(0),
	);
}
export function voteKey(id: string) {
	let h = 2166136261;
	for (const c of id) {
		h ^= c.charCodeAt(0);
		h = Math.imul(h, 16777619);
	}
	return (h >>> 0).toString(36);
}
export function selectShare(
	trip: TripWorkspace,
	stay = false,
	short = false,
): TripShare {
	const shareId = trip.shareId ?? crypto.randomUUID(),
		revision = (trip.shareRevision ?? 0) + 1;
	const copy = createTripWorkspace(
		{
			title: `${trip.destinationCity} 일행 일정`,
			origin: trip.origin,
			destination: trip.destination,
			destinationCity: trip.destinationCity,
			startDate: trip.startDate,
			endDate: trip.endDate,
			partySize: trip.partySize,
			styles: [],
			dailyBudgetJpy: 0,
		},
		trip.createdAt,
		shareId,
	);
	copy.days = trip.days.map((d) => ({
		date: d.date,
		items: d.items
			.filter((i) => !i.deferred)
			.map((i) => ({
				id: i.id,
				type: i.type,
				title: i.title,
				area: short ? "" : i.area,
				time: short ? null : i.time,
				estimateJpy: null,
				mapQuery: null,
				note: null,
				completed: false,
			})),
	}));
	const keys = copy.days.flatMap((d) => d.items.map((i) => voteKey(i.id)));
	if (new Set(keys).size !== keys.length) throw new Error("SHARE_ID_COLLISION");
	copy.party = trip.party?.map((p) => ({ ...p }));
	copy.checklist = trip.checklist
		.filter((i) => i.assignee)
		.map((i) => ({ ...i, done: false, officialUrl: null }));
	if (stay && trip.stay)
		copy.stay = {
			name: trip.stay.name,
			addressJa: trip.stay.addressJa,
			note: "",
		};
	if (!isTripWorkspace(copy)) throw new Error("INVALID_SHARE");
	return { kind: "trip", trip: copy, shareId, revision };
}
export async function encodeShare(payload: SharePayload, compress = true) {
	const raw = encoder.encode(JSON.stringify(payload));
	let data = raw,
		prefix =
			payload.kind === "trip" ? "t0" : payload.kind === "reply" ? "r0" : "p0";
	if (compress && typeof CompressionStream !== "undefined") {
		try {
			data = new Uint8Array(
				await new Response(
					new Blob([raw])
						.stream()
						.pipeThrough(new CompressionStream("deflate-raw")),
				).arrayBuffer(),
			);
			prefix = prefix.replace("0", "1");
		} catch {}
	}
	return prefix + "." + base64(data);
}
export function extractShare(input: string) {
	const match = input.match(/(?:[trp][01])\.[A-Za-z0-9_-]+/);
	if (match) return match[0];
	try {
		const u = new URL(input.trim());
		const data = u.searchParams.get("d");
		if (data && /^[trp][01]\./.test(data)) return data;
	} catch {}
	throw new Error("INVALID_SHARE");
}
export async function decodeShare(input: string): Promise<SharePayload> {
	const code = extractShare(input),
		[prefix, body] = code.split(".");
	let raw = bytes(body!);
	if (prefix!.endsWith("1")) {
		const reader = new Blob([raw])
			.stream()
			.pipeThrough(new DecompressionStream("deflate-raw"))
			.getReader();
		const chunks: Uint8Array[] = [];
		let size = 0;
		try {
			while (true) {
				const part = await reader.read();
				if (part.done) break;
				size += part.value.length;
				if (size > 512000) {
					await reader.cancel();
					throw new Error("SHARE_TOO_LARGE");
				}
				chunks.push(part.value);
			}
			raw = new Uint8Array(size);
			let n = 0;
			for (const chunk of chunks) {
				raw.set(chunk, n);
				n += chunk.length;
			}
		} finally {
			reader.releaseLock();
		}
	}
	if (raw.byteLength > 512000) throw new Error("SHARE_TOO_LARGE");
	const v = JSON.parse(new TextDecoder("utf-8", { fatal: true }).decode(raw));
	const text = (x: unknown, n: number) =>
		typeof x === "string" && x.length > 0 && x.length <= n;
	if (
		prefix!.startsWith("t") &&
		v?.kind === "trip" &&
		text(v.shareId, 128) &&
		Number.isInteger(v.revision) &&
		v.revision > 0 &&
		v.revision <= 1000000 &&
		isTripWorkspace(v.trip)
	)
		return selectReceived(v);
	if (
		prefix!.startsWith("r") &&
		v?.kind === "reply" &&
		text(v.shareId, 128) &&
		text(v.nick, 10) &&
		Array.isArray(v.marks) &&
		v.marks.length <= 500 &&
		v.marks.every(
			(r: unknown) =>
				Array.isArray(r) &&
				r.length === 2 &&
				text(r[0], 8) &&
				["yes", "skip"].includes(r[1]),
		)
	)
		return { kind: "reply", shareId: v.shareId, nick: v.nick, marks: v.marks };
	if (
		prefix!.startsWith("p") &&
		v?.kind === "payment" &&
		Array.isArray(v.rows) &&
		v.rows.length <= 12 &&
		v.rows.every(
			(r: Record<string, unknown>) =>
				r &&
				text(r.from, 10) &&
				text(r.to, 10) &&
				typeof r.amountJpy === "number" &&
				Number.isInteger(r.amountJpy) &&
				r.amountJpy > 0 &&
				r.amountJpy <= 1000000000,
		)
	)
		return { kind: "payment", rows: v.rows };
	throw new Error("INVALID_SHARE");
}
// Even a malicious link cannot smuggle photos, arbitrary fields or health details into local storage.
function selectReceived(v: TripShare): TripShare {
	const safe = selectShare(
		{ ...v.trip, shareId: v.shareId, shareRevision: v.revision - 1 },
		Boolean(v.trip.stay),
	);
	return { ...safe, revision: v.revision };
}
export function sharedTripCopy(payload: TripShare) {
	return {
		...structuredClone(payload.trip),
		id: crypto.randomUUID(),
		sharedFrom: {
			shareId: payload.shareId,
			revision: payload.revision,
			receivedAt: new Date().toISOString(),
		},
		updatedAt: new Date().toISOString(),
	};
}
export function shareDifferences(local: TripWorkspace, next: TripWorkspace) {
	const a = local.days.flatMap((d) =>
			d.items.map((i) => ({ ...i, date: d.date })),
		),
		b = next.days.flatMap((d) => d.items.map((i) => ({ ...i, date: d.date })));
	return [
		...b.flatMap((i) => {
			const old = a.find((x) => x.id === i.id);
			return !old
				? [`추가 · ${i.title}`]
				: old.date !== i.date || old.time !== i.time
					? [`시간·날짜 변경 · ${i.title}`]
					: [];
		}),
		...a
			.filter((i) => !b.some((x) => x.id === i.id))
			.map((i) => `빠짐 · ${i.title}`),
	];
}
export function updateSharedTrip(local: TripWorkspace, payload: TripShare) {
	if (
		!local.sharedFrom ||
		local.sharedFrom.shareId !== payload.shareId ||
		local.sharedFrom.revision >= payload.revision
	)
		throw new Error("STALE_SHARE");
	const copy = structuredClone(local);
	const before = copy.days.flatMap((d) => d.items);
	copy.days = payload.trip.days.map((d) => ({
		date: d.date,
		items: d.items.map((i) => {
			const old = before.find((x) => x.id === i.id);
			return {
				...i,
				note: old?.note ?? null,
				completed: old?.completed ?? false,
				returnReason: old?.returnReason,
			};
		}),
	}));
	copy.startDate = payload.trip.startDate;
	copy.endDate = payload.trip.endDate;
	copy.filmMoments = copy.filmMoments?.map((m) => {
		const day = copy.days.find((d) =>
			d.items.some((i) => i.id === m.planItemId),
		);
		return {
			...m,
			dayDate: day?.date ?? copy.startDate,
			planItemId: day ? m.planItemId : null,
		};
	});
	copy.lastAdjustment = null;
	copy.lastReplacement = null;
	copy.sharedFrom = { ...local.sharedFrom, revision: payload.revision };
	if (!isTripWorkspace(copy)) throw new Error("INVALID_SHARE");
	return copy;
}
export function mergeReply(local: TripWorkspace, reply: ReplyShare) {
	if (local.shareId !== reply.shareId) throw new Error("UNKNOWN_SHARE");
	const copy = structuredClone(local);
	copy.votes ??= {};
	for (const [key, value] of reply.marks) {
		const item = copy.days
			.flatMap((d) => d.items)
			.find((i) => voteKey(i.id) === key);
		if (!item) continue;
		copy.votes[item.id] = [
			...(copy.votes[item.id] ?? []).filter((v) => v.nick !== reply.nick),
			{ nick: reply.nick, value, at: new Date().toISOString() },
		];
	}
	if (!isTripWorkspace(copy)) throw new Error("INVALID_REPLY");
	return copy;
}
export function tripShareText(trip: TripWorkspace) {
	return trip.days
		.map(
			(d, i) =>
				`${i + 1}일차 ${d.date}\n${d.items.map((p) => `${p.time ?? ""} ${p.title}`).join("\n")}`,
		)
		.join("\n\n");
}
